/**
 * AWS Lambda Function Utilities
 * Handles direct Lambda function invocations for game logic and data management
 */

import { AWS_CONFIG } from './config';
import { 
  GameSession, 
  Move, 
  GameResult, 
  Profile,
  RoomCode,
  LobbyGame,
  GameId,
  PlayerId 
} from '../types';

// Lambda client will be initialized when AWS SDK is available
let lambdaClient: any = null;

/**
 * Initialize Lambda client
 */
export const initializeLambda = async () => {
  try {
    // These imports will be available once AWS SDK is installed
    const { LambdaClient } = await import('@aws-sdk/client-lambda');
    
    lambdaClient = new LambdaClient({ region: AWS_CONFIG.region });
    
    console.log('Lambda client initialized successfully');
    return true;
  } catch (error) {
    console.error('Failed to initialize Lambda client:', error);
    return false;
  }
};

/**
 * Base Lambda invocation utility
 */
class LambdaInvoker {
  /**
   * Invoke a Lambda function
   */
  static async invoke<T>(functionName: string, payload: any): Promise<T | null> {
    if (!lambdaClient) {
      console.error('Lambda client not initialized');
      return null;
    }

    try {
      const { InvokeCommand } = await import('@aws-sdk/client-lambda');
      
      const command = new InvokeCommand({
        FunctionName: functionName,
        Payload: JSON.stringify(payload),
        InvocationType: 'RequestResponse'
      });

      const response = await lambdaClient.send(command);
      
      if (response.Payload) {
        const result = JSON.parse(new TextDecoder().decode(response.Payload));
        
        // Check for Lambda execution errors
        if (response.FunctionError) {
          console.error('Lambda function error:', result);
          return null;
        }
        
        return result as T;
      }
      
      return null;
    } catch (error) {
      console.error('Lambda invocation error:', error);
      return null;
    }
  }
}

/**
 * Game Logic Lambda Functions
 */
export class GameLogicLambda {
  private static functionName = AWS_CONFIG.lambda.gameLogic;

  /**
   * Create a new game session
   */
  static async createGame(gameData: Partial<GameSession>): Promise<GameSession | null> {
    return LambdaInvoker.invoke<GameSession>(this.functionName, {
      action: 'createGame',
      data: gameData
    });
  }

  /**
   * Make a move in a game
   */
  static async makeMove(gameId: GameId, move: Move): Promise<GameSession | null> {
    return LambdaInvoker.invoke<GameSession>(this.functionName, {
      action: 'makeMove',
      gameId,
      move
    });
  }

  /**
   * Get game state
   */
  static async getGameState(gameId: GameId): Promise<GameSession | null> {
    return LambdaInvoker.invoke<GameSession>(this.functionName, {
      action: 'getGameState',
      gameId
    });
  }

  /**
   * Validate move
   */
  static async validateMove(gameId: GameId, move: Move): Promise<boolean> {
    const result = await LambdaInvoker.invoke<{ valid: boolean }>(this.functionName, {
      action: 'validateMove',
      gameId,
      move
    });
    
    return result?.valid || false;
  }

  /**
   * Check win condition
   */
  static async checkWinCondition(gameId: GameId): Promise<any> {
    return LambdaInvoker.invoke(this.functionName, {
      action: 'checkWinCondition',
      gameId
    });
  }

  /**
   * End game session
   */
  static async endGame(gameId: GameId, result: GameResult): Promise<boolean> {
    const response = await LambdaInvoker.invoke<{ success: boolean }>(this.functionName, {
      action: 'endGame',
      gameId,
      result
    });
    
    return response?.success || false;
  }
}

/**
 * User Management Lambda Functions
 */
export class UserManagementLambda {
  private static functionName = AWS_CONFIG.lambda.userManagement;

  /**
   * Create user profile
   */
  static async createProfile(profileData: Partial<Profile>): Promise<Profile | null> {
    return LambdaInvoker.invoke<Profile>(this.functionName, {
      action: 'createProfile',
      data: profileData
    });
  }

  /**
   * Get user profile
   */
  static async getProfile(userId: string): Promise<Profile | null> {
    return LambdaInvoker.invoke<Profile>(this.functionName, {
      action: 'getProfile',
      userId
    });
  }

  /**
   * Update user profile
   */
  static async updateProfile(userId: string, profileData: Partial<Profile>): Promise<Profile | null> {
    return LambdaInvoker.invoke<Profile>(this.functionName, {
      action: 'updateProfile',
      userId,
      data: profileData
    });
  }

  /**
   * Sync user data across devices
   */
  static async syncUserData(userId: string, localData: any): Promise<Profile | null> {
    return LambdaInvoker.invoke<Profile>(this.functionName, {
      action: 'syncUserData',
      userId,
      localData
    });
  }

  /**
   * Record game result
   */
  static async recordGameResult(userId: string, gameResult: GameResult): Promise<boolean> {
    const response = await LambdaInvoker.invoke<{ success: boolean }>(this.functionName, {
      action: 'recordGameResult',
      userId,
      gameResult
    });
    
    return response?.success || false;
  }
}

/**
 * Multiplayer Manager Lambda Functions
 */
export class MultiplayerManagerLambda {
  private static functionName = AWS_CONFIG.lambda.multiplayerManager;

  /**
   * Create room code
   */
  static async createRoomCode(): Promise<RoomCode | null> {
    return LambdaInvoker.invoke<RoomCode>(this.functionName, {
      action: 'createRoomCode'
    });
  }

  /**
   * Validate room code
   */
  static async validateRoomCode(code: string): Promise<RoomCode | null> {
    return LambdaInvoker.invoke<RoomCode>(this.functionName, {
      action: 'validateRoomCode',
      code
    });
  }

  /**
   * Join room
   */
  static async joinRoom(code: string, playerId: PlayerId): Promise<GameSession | null> {
    return LambdaInvoker.invoke<GameSession>(this.functionName, {
      action: 'joinRoom',
      code,
      playerId
    });
  }

  /**
   * Create lobby game
   */
  static async createLobbyGame(gameData: Partial<LobbyGame>): Promise<LobbyGame | null> {
    return LambdaInvoker.invoke<LobbyGame>(this.functionName, {
      action: 'createLobbyGame',
      data: gameData
    });
  }

  /**
   * Get lobby games
   */
  static async getLobbyGames(): Promise<LobbyGame[]> {
    const result = await LambdaInvoker.invoke<{ games: LobbyGame[] }>(this.functionName, {
      action: 'getLobbyGames'
    });
    
    return result?.games || [];
  }

  /**
   * Join lobby game
   */
  static async joinLobbyGame(gameId: GameId, playerId: PlayerId): Promise<GameSession | null> {
    return LambdaInvoker.invoke<GameSession>(this.functionName, {
      action: 'joinLobbyGame',
      gameId,
      playerId
    });
  }

  /**
   * Leave game
   */
  static async leaveGame(gameId: GameId, playerId: PlayerId): Promise<boolean> {
    const response = await LambdaInvoker.invoke<{ success: boolean }>(this.functionName, {
      action: 'leaveGame',
      gameId,
      playerId
    });
    
    return response?.success || false;
  }

  /**
   * Clean up expired rooms
   */
  static async cleanupExpiredRooms(): Promise<boolean> {
    const response = await LambdaInvoker.invoke<{ success: boolean }>(this.functionName, {
      action: 'cleanupExpiredRooms'
    });
    
    return response?.success || false;
  }
}

/**
 * Statistics Manager Lambda Functions
 */
export class StatisticsManagerLambda {
  private static functionName = AWS_CONFIG.lambda.statisticsManager;

  /**
   * Get user statistics
   */
  static async getUserStatistics(userId: string): Promise<any> {
    return LambdaInvoker.invoke(this.functionName, {
      action: 'getUserStatistics',
      userId
    });
  }

  /**
   * Get global leaderboard
   */
  static async getGlobalLeaderboard(gameMode?: string, difficulty?: string): Promise<any[]> {
    const result = await LambdaInvoker.invoke<{ leaderboard: any[] }>(this.functionName, {
      action: 'getGlobalLeaderboard',
      gameMode,
      difficulty
    });
    
    return result?.leaderboard || [];
  }

  /**
   * Get user ranking
   */
  static async getUserRanking(userId: string, gameMode?: string, difficulty?: string): Promise<number | null> {
    const result = await LambdaInvoker.invoke<{ ranking: number }>(this.functionName, {
      action: 'getUserRanking',
      userId,
      gameMode,
      difficulty
    });
    
    return result?.ranking || null;
  }

  /**
   * Update statistics
   */
  static async updateStatistics(userId: string, gameResult: GameResult): Promise<boolean> {
    const response = await LambdaInvoker.invoke<{ success: boolean }>(this.functionName, {
      action: 'updateStatistics',
      userId,
      gameResult
    });
    
    return response?.success || false;
  }

  /**
   * Get game history
   */
  static async getGameHistory(userId: string, limit?: number, offset?: number): Promise<GameResult[]> {
    const result = await LambdaInvoker.invoke<{ history: GameResult[] }>(this.functionName, {
      action: 'getGameHistory',
      userId,
      limit,
      offset
    });
    
    return result?.history || [];
  }

  /**
   * Calculate win rates
   */
  static async calculateWinRates(userId: string): Promise<any> {
    return LambdaInvoker.invoke(this.functionName, {
      action: 'calculateWinRates',
      userId
    });
  }
}